/**
 * Helpers IPv4 para autocompletar gateway/pool desde un CIDR.
 *
 * Ejemplos:
 *   parseCidr("172.16.0.0/24")  → { network: "172.16.0.0", prefix: 24, hosts: 254 }
 *   defaultsFromCidr("172.16.0.0/24") → {
 *     gateway:    "172.16.0.1",
 *     pool_start: "172.16.0.2",
 *     pool_end:   "172.16.0.254",
 *   }
 */

export type CidrInfo = {
  network: string;
  broadcast: string;
  prefix: number;
  total: number;    // 2^(32-prefix)
  usable: number;   // total - 2 (network + broadcast)
};

function ipToInt(ip: string): number {
  const parts = ip.split(".").map(Number);
  if (parts.length !== 4 || parts.some((p) => isNaN(p) || p < 0 || p > 255)) {
    throw new Error(`IP inválida: ${ip}`);
  }
  return ((parts[0] << 24) | (parts[1] << 16) | (parts[2] << 8) | parts[3]) >>> 0;
}

function intToIp(n: number): string {
  return [
    (n >>> 24) & 0xff,
    (n >>> 16) & 0xff,
    (n >>> 8) & 0xff,
    n & 0xff,
  ].join(".");
}

/** Parsea un CIDR y devuelve información básica. Retorna null si inválido. */
export function parseCidr(cidr: string): CidrInfo | null {
  const m = /^(\d{1,3}(?:\.\d{1,3}){3})\/(\d{1,2})$/.exec(cidr.trim());
  if (!m) return null;
  try {
    const ipInt = ipToInt(m[1]);
    const prefix = Number(m[2]);
    if (prefix < 0 || prefix > 32) return null;
    const mask = prefix === 0 ? 0 : (0xffffffff << (32 - prefix)) >>> 0;
    const network = ipInt & mask;
    const broadcast = (network | (~mask >>> 0)) >>> 0;
    const total = 2 ** (32 - prefix);
    const usable = Math.max(0, total - 2);
    return {
      network: intToIp(network),
      broadcast: intToIp(broadcast),
      prefix,
      total,
      usable,
    };
  } catch {
    return null;
  }
}

/**
 * Calcula defaults sensatos para una red desde su CIDR.
 * Si el prefix es /31 o /32 (punto a punto / host) devuelve null porque no hay pool sensato.
 */
export function defaultsFromCidr(cidr: string): {
  gateway: string;
  pool_start: string;
  pool_end: string;
} | null {
  const info = parseCidr(cidr);
  if (!info || info.prefix > 30) return null;
  const netInt = ipToInt(info.network);
  const bcastInt = ipToInt(info.broadcast);
  return {
    gateway: intToIp(netInt + 1),
    pool_start: intToIp(netInt + 2),
    pool_end: intToIp(bcastInt - 1),
  };
}

/** Validación rápida de un CIDR (true/false). */
export function isValidCidr(cidr: string): boolean {
  return parseCidr(cidr) !== null;
}

/** Validación de una IP v4. */
export function isValidIp(ip: string): boolean {
  try {
    ipToInt(ip);
    return true;
  } catch {
    return false;
  }
}
